import invert from 'lodash/invert'

import { adapterMapper } from '../helpers/AdapterMapper'

const Brainstormer = {
  id: 'id',
  title: 'name',
  description: 'description',
  permaLink: 'permalink',
  state: 'state',
  tags: 'tag-list',
  allowComments: 'allow-comments-for-ideas',
  allowImageUpload: 'allow-file-upload-for-ideas',
  archivalMessage: 'archival-reason-message',
  notifyAdmins: 'send-acknowledgement-to-admins',
  notifyParticipant: 'send-acknowledgement',
  allowUnverifiedParticipation: 'allow-unverified-participation',
  allowReactions: 'disable-voting',
  limitVoting: 'voting-limit',
  scheduledAt: 'scheduled-at',
  startDate: 'voting-start-at',
  endDate: 'voting-end-at',
  createdAt: 'created-at',
  ideasCount: 'ideas-count',
  imageUrl: 'image-url',
  notificationsEmails: 'notifications-emails',
  sequence: 'sequence',
  sticked: 'sticked',
  updatedAt: 'updated-at',
}

/**
 * parses the input data object by mapping and restructuring its properties
 * based on specific conditions and rules.
 *
 * first - it extracts dependent properties with a boolean value,
 * and adds its corresponding keys to the parsable only with boolean value is true.
 * This is to make sure only the properties that are needed are passed to the adapterMapper.
 *
 * @param data - Object containing the data to be parsed. The keys and values of Ideas Tool state object.
 *
 * @returns A new object containing the parsed data, where keys and values
 *          are strings, mapped using the `adapterMapper` function and the `Brainstormer` adapter.
 */
export const parseToAPI = (data: Record<string, any | any[]>) => {
  const {
    limitVoting,
    limitVotingCount,
    allowScheduleReactions,
    scheduledReactionsDateRange,
    adminEmails,
    ...parsable
  } = data

  const { allowReactions, notifyAdmins } = parsable

  // initializing the parsable object with empty values to avoid previous selected values in case of edit.
  parsable['startDate'] = null
  parsable['endDate'] = null
  parsable['notificationsEmails'] = null
  parsable['allowReactions'] = !allowReactions
  parsable['limitVoting'] = null

  /**
   * This can be removed once we move away from BH project page
   * Keeping this here for the time being to avoid breaking changes
   * and to make sure the existing projects are not affected.
   * Reference: https://github.com/bangthetable/blue-haven/blob/develop/src/api/brainstormer.js#L37
   */
  parsable['notifyAdmins'] =
    typeof notifyAdmins === 'boolean' ? notifyAdmins : false

  // Extracting dependent properties under allowReactions toggle
  if (allowReactions) {
    if (limitVoting) {
      parsable['limitVoting'] = limitVotingCount
    }
    if (allowScheduleReactions && scheduledReactionsDateRange) {
      parsable['startDate'] = scheduledReactionsDateRange[0]
      parsable['endDate'] = scheduledReactionsDateRange[1]
    }
  }
  // Extracting dependent properties under notifyAdmins toggle
  if (notifyAdmins) {
    parsable['notificationsEmails'] =
      adminEmails && Array.isArray(adminEmails) ? adminEmails.join(',') : null
  }

  const parsed: Record<string, string> = adapterMapper(parsable, Brainstormer)
  return parsed
}

export const parseToAppState = (data: Record<string, any>) => {
  const {
    'disable-voting': allowReactions,
    'voting-limit': limitVoting,
    'voting-start-at': startDate,
    'voting-end-at': endDate,
    'notifications-emails': notificationsEmails,
  } = data
  const parsed: Record<string, any> = adapterMapper(data, invert(Brainstormer))

  parsed['allowReactions'] = !allowReactions

  if (startDate && endDate) {
    parsed['allowScheduleReactions'] = true
    parsed['scheduledReactionsDateRange'] = [startDate, endDate]
  }

  if (limitVoting) {
    parsed['limitVoting'] = true
    parsed['limitVotingCount'] = limitVoting
  }

  if (notificationsEmails) {
    parsed['adminEmails'] = notificationsEmails.split(',')
  }

  return parsed
}

export default Brainstormer
